import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import { Box, Button, CardActions } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import OutlinedInput from '@mui/material/OutlinedInput'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import ListItemText from '@mui/material/ListItemText'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Checkbox from '@mui/material/Checkbox'

import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { downloadFile } from 'src/v3/utilities/filesystem'
import {
  errorMessageChanged,
  fetchLiveClasses,
  selectLiveClasses,
  selectLiveClassesByNames,
  selectLiveClassesStatus,
} from '../../admin.slice'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  },
}

export default () => {
  let dispatch = useAppDispatch()
  let liveClasses = useAppSelector(selectLiveClasses)
  let liveClassesStatus = useAppSelector(selectLiveClassesStatus)
  let [liveClassName, setLiveClassName] = useState<string[]>([])
  let liveClassesByNames = useAppSelector(
    selectLiveClassesByNames(liveClassName),
  )
  let [startDate, setStartDate] = useState(dayjs(new Date()))
  let [endDate, setEndDate] = useState(dayjs(new Date()))
  let [exportStatus, setExportStatus] = useState<
    'uninitialized' | 'exporting' | 'exported'
  >('uninitialized')

  let handleChange = (event: SelectChangeEvent<typeof liveClassName>) => {
    let {
      target: { value },
    } = event
    setLiveClassName(typeof value === 'string' ? value.split(',') : value)
  }

  useEffect(() => {
    if (liveClassesStatus === 'uninitialized') {
      dispatch(fetchLiveClasses())
    }
  }, [liveClassesStatus])

  let submit = () => {
    setExportStatus('exporting')
    axios
      .post('/api/v2/admin/questionnaire/export/live', {
        classIDs: liveClassesByNames.map((c) => c.id),
        startDate,
        endDate,
      })
      .then(pluckData)
      .then((response: any) => downloadFile(response, 'questionnaires.csv'))
      .catch((err) =>
        dispatch(
          errorMessageChanged(
            `CSV Export Failed: ${err.response?.data || err}`,
          ),
        ),
      )
      .then(() => setExportStatus('exported'))
      .catch(() => setExportStatus('uninitialized'))
  }

  return (
    <Card sx={{ p: 2 }}>
      <Typography
        variant="h6"
        sx={{ fontFamily: 'revert', textAlign: 'center' }}
      >
        Export Live Class Questionnaires
      </Typography>
      <div className="flex flex-col justify-center mt-2">
        <FormControl sx={{ width: '100%' }}>
          <InputLabel id="demo-multiple-checkbox-label">Tag</InputLabel>
          <Select
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={liveClassName}
            onChange={handleChange}
            input={<OutlinedInput label="Tag" />}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {liveClasses.map((c) => (
              <MenuItem key={c.id} value={c.name}>
                <Checkbox checked={liveClassName.indexOf(c.name) > -1} />
                <ListItemText primary={c.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ pt: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: '100%' }}
              label="Start Date"
              value={startDate}
              onChange={(v) => setStartDate(v as dayjs.Dayjs)}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ pt: 4 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              sx={{ width: '100%' }}
              label="End Date"
              value={endDate}
              onChange={(v) => setEndDate(v as dayjs.Dayjs)}
            />
          </LocalizationProvider>
        </Box>
      </div>
      <CardActions>
        <Button
          disabled={exportStatus === 'exporting'}
          variant="outlined"
          color="primary"
          fullWidth
          onClick={submit}
        >
          {exportStatus === 'exporting'
            ? 'Exporting...'
            : exportStatus === 'exported'
              ? 'Exported'
              : 'Export'}
        </Button>
      </CardActions>
    </Card>
  )
}
